import React, { useState, useEffect } from 'react';
import Alert from "@amzn/awsui-components-core/alert";
import Badge from "@amzn/awsui-components-core/badge";
import Box from "@amzn/awsui-components-core/box";
import Button from "@amzn/awsui-components-core/button";
import Container from "@amzn/awsui-components-core/container";
import FormField from "@amzn/awsui-components-core/form-field";
import Icon from "@amzn/awsui-components-core/icon";
import Modal from "@amzn/awsui-components-core/modal";
import SpaceBetween from "@amzn/awsui-components-core/space-between";
import Spinner from "@amzn/awsui-components-core/spinner";
import ActionButtons from 'src/components/shared/ActionButtons';
import RichTextEditor from 'src/components/shared/RichTextEditor';
import RichTextContent from 'src/components/shared/RichTextContent';
import { fetchShiftPassdown, createShiftPassdown, updateShiftPassdown, deleteShiftPassdown, ShiftPassdownEntry, CreateShiftPassdownInput } from 'src/services/passdowns-api';
import { useUser } from 'src/context/UserContext';
import { saveDraft, loadDraft, clearDraft } from 'src/utils/formDraft';
import FormattedDate from 'src/components/shared/FormattedDate';
import { useAlertMessages } from 'src/utils/useAlertMessages';
import { formatPlainTextToHtml } from 'src/utils/helpers';
import { MESSAGES } from 'src/utils/errorMessages';
import { passdownHistoryStyles } from 'src/components/shared/design-tokens';
import { requiredMark, requiredFieldHint } from 'src/components/shared/formHelpers';
import mockData from 'src/mock/mockData.json';

const DRAFT_KEY = 'passdown-alarms';
const USE_MOCK_DATA = false;

/** Strips HTML tags to test whether rich-text content is actually present. */
const richTextToPlain = (html?: string) => (html || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim();

interface AlarmsFormData {
    category: string;
    alarmsSlaDefects: string;
    alarmsBolosPEs: string;
    alarmsEscalationsNextSteps: string;
    alarmsIssuesBlockers: string;
    alarmsNextForgeAudits: string;
    // Tiptap ProseMirror JSON mirrors of the rich-text fields above.
    alarmsSlaDefectsJson?: Record<string, any>;
    alarmsBolosPEsJson?: Record<string, any>;
    alarmsEscalationsNextStepsJson?: Record<string, any>;
    alarmsIssuesBlockersJson?: Record<string, any>;
    alarmsNextForgeAuditsJson?: Record<string, any>;
}

const defaultForm: AlarmsFormData = {
    category: 'ALARMS',
    alarmsSlaDefects: '',
    alarmsBolosPEs: '',
    alarmsEscalationsNextSteps: '',
    alarmsIssuesBlockers: '',
    alarmsNextForgeAudits: '',
};

const AlarmsPassdown = ({ showForm, setShowForm }: { showForm: boolean; setShowForm: (v: boolean) => void }) => {
    const { canModify } = useUser();
    const [passdowns, setPassdowns] = useState<ShiftPassdownEntry[]>([]);
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [editingId, setEditingId] = useState<string | null>(null);
    const [expandedId, setExpandedId] = useState<string | null>(null);
    const [activeExpanded, setActiveExpanded] = useState(true);
    const [showAllHistory, setShowAllHistory] = useState(false);
    const [form, setForm] = useState<AlarmsFormData>({ ...defaultForm });
    const { error, successMsg, setError, setSuccessMsg, alertRef } = useAlertMessages();
    const [deleteTarget, setDeleteTarget] = useState<{ id: string; createdBy: string } | null>(null);

    const updateField = (field: keyof AlarmsFormData, value: string | number | Record<string, any>) => {
        setForm(prev => ({ ...prev, [field]: value }));
    };

    useEffect(() => {
        loadPassdowns();
        const draft = loadDraft(DRAFT_KEY);
        if (draft) {
            setForm({ ...defaultForm, ...draft });
            setEditingId(draft.editingId || null);
            setShowForm(true);
            clearDraft(DRAFT_KEY);
        }
    }, []);

    const loadPassdowns = async () => {
        setLoading(true);
        try {
            if (USE_MOCK_DATA) {
                setPassdowns((mockData.alarmspassdowns?.items || []) as ShiftPassdownEntry[]);
            } else {
                const data = await fetchShiftPassdown('ALARMS');
                setPassdowns(data.items || []);
            }
        } catch (err: any) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    const handleEdit = (b: ShiftPassdownEntry) => {
        setEditingId(b.passdownId);
        setForm({
            category: 'ALARMS',
            alarmsSlaDefects: b.alarmsSlaDefects || '',
            alarmsBolosPEs: b.alarmsBolosPEs || '',
            alarmsEscalationsNextSteps: b.alarmsEscalationsNextSteps || '',
            alarmsIssuesBlockers: b.alarmsIssuesBlockers || '',
            alarmsNextForgeAudits: b.alarmsNextForgeAudits || '',
            alarmsSlaDefectsJson: b.alarmsSlaDefectsJson,
            alarmsBolosPEsJson: b.alarmsBolosPEsJson,
            alarmsEscalationsNextStepsJson: b.alarmsEscalationsNextStepsJson,
            alarmsIssuesBlockersJson: b.alarmsIssuesBlockersJson,
            alarmsNextForgeAuditsJson: b.alarmsNextForgeAuditsJson,
        });
        setShowForm(true);
    };

    const resetForm = () => {
        setShowForm(false);
        setEditingId(null);
        setForm({ ...defaultForm });
        clearDraft(DRAFT_KEY);
    };

    const handleDeleteClick = (b: ShiftPassdownEntry) => {
        setDeleteTarget({ id: b.passdownId, createdBy: b.createdBy });
    };

    const confirmDelete = async () => {
        if (!deleteTarget) return;
        try {
            await deleteShiftPassdown(deleteTarget.id);
            setSuccessMsg('Alarms Passdown deleted');
            await loadPassdowns();
        } catch (err: any) {
            setError(MESSAGES.RECORD_DELETE_ERROR);
        }
        setDeleteTarget(null);
    };

    const handleSubmit = async () => {
        setSubmitting(true);
        try {
            const input: CreateShiftPassdownInput = { ...form };
            const isUpdate = !!editingId;
            if (editingId) { await updateShiftPassdown(editingId, input); }
            else { await createShiftPassdown(input); }
            resetForm();
            setSuccessMsg(isUpdate ? 'Alarms Passdown updated' : 'Alarms Passdown created');
            await loadPassdowns();
        } catch (err: any) {
            const msg = err.message || '';
            if (msg.includes('Not authenticated') || msg.includes('403')) {
                saveDraft(DRAFT_KEY, { ...form, editingId });
            }
            setError(editingId ? MESSAGES.RECORD_UPDATE_ERROR : MESSAGES.RECORD_CREATE_ERROR);
        } finally { setSubmitting(false); }
    };

    // Render an Alarms rich-text field read-only. Prefers the Tiptap JSON when
    // present (rendered safely via RichTextContent); otherwise falls back to the
    // legacy plain-text string so older records still render as before.
    const renderRichField = (label: string, json?: Record<string, any>, value?: string, exactCase = false) => {
        // `value` is the plain-text mirror of the JSON (both are emitted together by
        // the editor and co-persisted), so it reliably reflects whether the field has
        // real content — including when the editor was cleared to an empty document
        // (which still emits a JSON doc). Hide empty fields, matching the original
        // renderField behavior.
        if (!richTextToPlain(value)) return null;
        return (
            <div className="passdown-entry__field">
                {/* exactCase disables the SCSS uppercase transform for labels whose
                    casing must be preserved verbatim (e.g. "BOLOs/PEs"). */}
                <h3 style={exactCase ? { textTransform: 'none' } : undefined}>{label}</h3>
                <div className="passdown-rich-text">
                    <RichTextContent json={json as any} html={formatPlainTextToHtml(value || '')} />
                </div>
            </div>
        );
    };

    const hasContent = [
        form.alarmsSlaDefects, form.alarmsBolosPEs, form.alarmsEscalationsNextSteps,
        form.alarmsIssuesBlockers, form.alarmsNextForgeAudits,
    ].every(val => !!richTextToPlain(val));
    const b0 = passdowns[0];

    return (
        <>
            <div ref={alertRef} tabIndex={-1}>
                { error &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="error"
                            onDismiss={() => setError(null)}
                        >
                            <strong>Error: </strong>{error}
                        </Alert>
                    </div>
                }
                { successMsg &&
                    <div className="alert">
                        <Alert
                            dismissible
                            type="success"
                            onDismiss={() => setSuccessMsg(null)}
                        >
                            {successMsg}
                        </Alert>
                    </div>
                }
            </div>

            {showForm && (
                <div className="passdown-form">
                    <FormField label={<>SLA Defects Last 24 Hours{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            autoFocus
                            value={form.alarmsSlaDefectsJson ?? formatPlainTextToHtml(form.alarmsSlaDefects || '')}
                            placeholder="SLA defects in the last 24 hours..."
                            onChangeText={(text) => updateField('alarmsSlaDefects', text)}
                            onChangeJson={(json) => updateField('alarmsSlaDefectsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>BOLOs/PEs{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.alarmsBolosPEsJson ?? formatPlainTextToHtml(form.alarmsBolosPEs || '')}
                            placeholder="BOLOs and PEs..."
                            onChangeText={(text) => updateField('alarmsBolosPEs', text)}
                            onChangeJson={(json) => updateField('alarmsBolosPEsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Escalations and Resolutions/Next Steps{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.alarmsEscalationsNextStepsJson ?? formatPlainTextToHtml(form.alarmsEscalationsNextSteps || '')}
                            placeholder="Escalations and resolutions/next steps..."
                            onChangeText={(text) => updateField('alarmsEscalationsNextSteps', text)}
                            onChangeJson={(json) => updateField('alarmsEscalationsNextStepsJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Issues/Blockers{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.alarmsIssuesBlockersJson ?? formatPlainTextToHtml(form.alarmsIssuesBlockers || '')}
                            placeholder="Issues or blockers..."
                            onChangeText={(text) => updateField('alarmsIssuesBlockers', text)}
                            onChangeJson={(json) => updateField('alarmsIssuesBlockersJson', json)}
                        />
                    </FormField>

                    <FormField label={<>Next Three FORGE Audits{requiredMark()}</>} stretch>
                        <RichTextEditor
                            key={editingId || 'new'}
                            value={form.alarmsNextForgeAuditsJson ?? formatPlainTextToHtml(form.alarmsNextForgeAudits || '')}
                            placeholder="Upcoming FORGE audits..."
                            onChangeText={(text) => updateField('alarmsNextForgeAudits', text)}
                            onChangeJson={(json) => updateField('alarmsNextForgeAuditsJson', json)}
                        />
                    </FormField>

                    <div className="passdown-form__actions">
                        {requiredFieldHint()}
                        <div className="passdown-form__actions-buttons">
                            <Button variant="link" onClick={resetForm}>Cancel</Button>
                            <Button
                                variant="primary"
                                onClick={handleSubmit}
                                disabled={submitting || !hasContent}
                            >
                                {submitting ? 'Submitting...' : (editingId ? 'Update Alarms Passdown' : 'Submit Alarms Passdown')}
                            </Button>
                        </div>
                    </div>
                </div>
            )}

            {loading && <Spinner />}
            {!loading && passdowns.length === 0 && !showForm && <p className="section-card__placeholder">No Alarms passdowns yet.</p>}

            {!loading && b0 && (
                <>
                    <div className="passdown-list">
                        <div className="passdown-entry">
                            <div className="passdown-entry__icon">
                                <span className="passdown-entry__meta">
                                    <FormattedDate date={b0.createdAt} /> &middot; by {b0.createdBy}
                                    <span className="badge-custom">
                                        <Badge color="green">Active</Badge>
                                    </span>
                                    { b0.updatedAt && b0.updatedAt !== b0.createdAt && (
                                        <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b0.updatedAt} /> &middot; by {b0.updatedBy}</span>
                                    )}
                                </span>
                                <div className="passdown-entry__actions">
                                    {canModify('alarmsShiftPassdown', b0.createdBy) && (
                                        <ActionButtons
                                            onEdit={() => handleEdit(b0)}
                                            onDelete={() => handleDeleteClick(b0)}
                                            disabled={showForm}
                                        />
                                    )}
                                    <span className="passdown-history__toggle" onClick={() => setActiveExpanded(prev => !prev)}>
                                        <Icon
                                            name={activeExpanded ? "caret-up-filled" : "caret-down-filled"}
                                            variant="subtle"
                                        />
                                    </span>
                                </div>
                            </div>

                            {!activeExpanded && <p style={{marginTop: "8px"}}>{richTextToPlain(b0.alarmsSlaDefects) || richTextToPlain(b0.alarmsEscalationsNextSteps)} ...</p>}

                            {activeExpanded && (
                                <div className="passdown-entry__fields-grid">
                                    {renderRichField('SLA Defects Last 24 Hours', b0.alarmsSlaDefectsJson, b0.alarmsSlaDefects)}
                                    {renderRichField('BOLOs/PEs', b0.alarmsBolosPEsJson, b0.alarmsBolosPEs, true)}
                                    {renderRichField('Escalations and Resolutions/Next Steps', b0.alarmsEscalationsNextStepsJson, b0.alarmsEscalationsNextSteps)}
                                    {renderRichField('Issues/Blockers', b0.alarmsIssuesBlockersJson, b0.alarmsIssuesBlockers)}
                                    {renderRichField('Next Three FORGE Audits', b0.alarmsNextForgeAuditsJson, b0.alarmsNextForgeAudits)}
                                </div>
                            )}
                        </div>
                    </div>

                    {passdowns.length > 1 && (
                        <div className="passdown-history">
                            <h4 className="passdown-history__title">📋 ALARMS PASSDOWN HISTORY</h4>
                            <div className="passdown-history__list">
                                {(showAllHistory ? passdowns.slice(1) : passdowns.slice(1, 6)).map((b, idx) => {
                                    const isExpanded = expandedId === b.passdownId;
                                    return (
                                        <span key={b.passdownId} style={{marginBottom: "0.8rem"}}>
                                            <Container style={passdownHistoryStyles}>
                                                <span className="passdown-history__dot" />
                                                <div className="passdown-history__content">
                                                    <div className="passdown-history__top">
                                                        <span className="passdown-history__date">
                                                            <FormattedDate date={b.createdAt} /> &middot; by {b.createdBy}
                                                            { b.updatedAt && b.updatedAt !== b.createdAt && (
                                                                <span className="passdown-entry__last-updated-info">Last updated on <FormattedDate date={b.updatedAt} /> &middot; by {b.updatedBy}</span>
                                                            )}
                                                        </span>
                                                        <div className="passdown-history__top-right">
                                                            {canModify('alarmsShiftPassdown', b.createdBy) && (
                                                                <ActionButtons
                                                                    onEdit={() => handleEdit(b)}
                                                                    onDelete={() => handleDeleteClick(b)}
                                                                    disabled={showForm}
                                                                />
                                                            )}
                                                            <span className="passdown-history__toggle" onClick={() => setExpandedId(isExpanded ? null : b.passdownId)}>
                                                                <Icon
                                                                    name={isExpanded ? "caret-up-filled" : "caret-down-filled"}
                                                                    variant="subtle"
                                                                />
                                                            </span>
                                                        </div>
                                                    </div>
                                                    {isExpanded && (
                                                        <div className="passdown-history__details">
                                                            <div className="passdown-entry__fields-grid">
                                                                {renderRichField('SLA Defects Last 24 Hours', b.alarmsSlaDefectsJson, b.alarmsSlaDefects)}
                                                                {renderRichField('BOLOs/PEs', b.alarmsBolosPEsJson, b.alarmsBolosPEs, true)}
                                                                {renderRichField('Escalations and Resolutions/Next Steps', b.alarmsEscalationsNextStepsJson, b.alarmsEscalationsNextSteps)}
                                                                {renderRichField('Issues/Blockers', b.alarmsIssuesBlockersJson, b.alarmsIssuesBlockers)}
                                                                {renderRichField('Next Three FORGE Audits', b.alarmsNextForgeAuditsJson, b.alarmsNextForgeAudits)}
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </Container>
                                        </span>
                                    );
                                })}
                            </div>
                            {passdowns.length > 6 && (
                                <div data-size='sm'>
                                    <Button
                                        variant="inline-link"
                                        ariaLabel={showAllHistory ? 'view less' : 'view more'}
                                        onClick={() => setShowAllHistory(prev => {
                                            if (prev) {
                                                setTimeout(() => alertRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' }), 0);
                                            }
                                            return !prev;
                                        })}
                                    >
                                        {showAllHistory ? 'View less' : 'View more'}
                                    </Button>
                                </div>
                            )}
                        </div>
                    )}
                </>
            )}

            {deleteTarget && (
                <Modal
                    visible
                    header="Delete Alarms Passdown"
                    onDismiss={() => setDeleteTarget(null)}
                    footer={
                        <Box float="right">
                            <SpaceBetween direction="horizontal" size="xs">
                                <Button variant="link" onClick={() => setDeleteTarget(null)}>Cancel</Button>
                                <Button variant="primary" onClick={confirmDelete}>Delete</Button>
                            </SpaceBetween>
                        </Box>
                    }
                >
                    Are you sure you want to delete this Alarms passdown by <strong>{deleteTarget.createdBy}</strong>? This cannot be undone.
                </Modal>
            )}
        </>
    );
};

export default AlarmsPassdown;
